/**
 * Shared Zod schemas for the auth flows (login + self-registration).
 *
 * Used by the admin login/register pages (client-side validation through
 * react-hook-form's `zodResolver`) and by the matching Fastify routes
 * (server-side body validation through `schema.safeParse`).
 *
 * Error `message` values are stable i18n keys (e.g. `auth.errors.emailRequired`).
 * The form renders them with `t(message)` so DE/EN copy lives in
 * `apps/admin/src/locales/{en,de}/auth.json`. The server returns the keys
 * as-is inside the existing `{ error, code, issues }` 400 envelope, where
 * `code: 'VALIDATION_FAILED'` lets the SPA branch on validation vs business
 * errors without parsing string contents.
 */
import { z } from 'zod';

/**
 * Credentials accepted by `POST /api/auth/login`.
 *
 * `force` is sent on the second attempt after the user confirms in the
 * OTHER_SESSION_ACTIVE prompt; it tells the server to drop any existing
 * sessions for this user before issuing the new JWT.
 *
 * `refreshToken` is the caller's currently-stored refresh token (if any). The
 * SPA sends it so the server can recognise a re-login from the SAME browser
 * and drop that session's family BEFORE the single-session count — otherwise
 * the user's own leftover/rotated session would falsely trip
 * OTHER_SESSION_ACTIVE against themselves.
 */
export const loginSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'auth.errors.emailRequired' })
    .email({ message: 'auth.errors.emailInvalid' }),
  password: z.string().min(1, { message: 'auth.errors.passwordRequired' }),
  force: z.boolean().optional(),
  refreshToken: z.string().optional(),
});

export type LoginInput = z.infer<typeof loginSchema>;

/**
 * Body shape for `POST /api/auth/register`. Self-registration always creates
 * a PASSENGER role (server-enforced); the schema is intentionally narrow —
 * `confirmPassword` is a client-only guard and is stripped before the request
 * leaves the SPA, so it does NOT belong in the server schema.
 */
export const registerServerSchema = z.object({
  firstName: z.string().trim().min(1, { message: 'auth.errors.firstNameRequired' }),
  lastName: z.string().trim().min(1, { message: 'auth.errors.lastNameRequired' }),
  email: z
    .string()
    .min(1, { message: 'auth.errors.emailRequired' })
    .email({ message: 'auth.errors.emailInvalid' }),
  phone: z.string().trim().min(1).optional(),
  password: z.string().min(8, { message: 'auth.errors.passwordTooShort' }),
  locale: z.enum(['en', 'de']).optional(),
});

export type RegisterServerInput = z.infer<typeof registerServerSchema>;

/**
 * Client-side registration form schema. Extends the server schema with
 * `confirmPassword` and a refinement pinning it to `password`. RHF defaults
 * keep every text field as `string` (never `undefined`) so the form-state
 * type and the schema input type line up exactly.
 */
export const registerFormSchema = registerServerSchema
  .extend({
    // Phone is kept as a required-string-in-form (possibly empty) so the
    // RHF defaultValue `''` matches the schema input. The submit handler
    // converts `''` → `undefined` before posting to the API.
    phone: z.string(),
    confirmPassword: z.string().min(1, { message: 'auth.errors.passwordRequired' }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'auth.errors.passwordMismatch',
    path: ['confirmPassword'],
  });

export type RegisterFormInput = z.infer<typeof registerFormSchema>;

/**
 * Credentials accepted by `POST /api/auth/token` — the machine-to-machine
 * endpoint external integrations use.
 *
 * Deliberately separate from {@link loginSchema}: the two endpoints accept
 * different principals (a SERVICE account here, a person there), issue
 * different token lifetimes, and only one of them creates a browser session.
 * Sharing one schema would invite sharing one handler, and the guards that
 * keep those principals apart are the whole point.
 *
 * `clientId` is the service account's email — it is already the unique login
 * identifier, so it doubles as the client id rather than adding a second
 * unique column.
 */
export const serviceTokenSchema = z.object({
  clientId: z
    .string()
    .min(1, { message: 'auth.errors.clientIdRequired' }),
  clientSecret: z
    .string()
    .min(1, { message: 'auth.errors.clientSecretRequired' }),
});

export type ServiceTokenInput = z.infer<typeof serviceTokenSchema>;

/**
 * Body accepted by `POST /api/service-accounts` — admin creating a machine
 * credential for one integration.
 *
 * `name` is a label, not an identity: the client id is derived from it so an
 * admin never has to invent an email address for a machine.
 *
 * ⚠️ `allowedIps` takes plain addresses, no CIDR. A service credential should
 * come from known hosts; a range is usually a sign it is being shared more
 * widely than intended. An empty list means "any address" — the API cannot
 * fall back to a Cloudflare firewall rule, because the API host is
 * gray-clouded and CF never sees this traffic.
 */
export const serviceAccountCreateSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { message: 'serviceAccounts.errors.nameRequired' })
    .max(60, { message: 'serviceAccounts.errors.nameTooLong' }),
  allowedIps: z.array(z.string().trim().min(1)).optional(),
});

/** Body accepted by `PATCH /api/service-accounts/:id`. */
export const serviceAccountUpdateSchema = z.object({
  name:       z.string().trim().min(1).max(60).optional(),
  allowedIps: z.array(z.string().trim().min(1)).optional(),
  /** ACTIVE re-enables a revoked account; INACTIVE revokes it. */
  status:     z.enum(['ACTIVE', 'INACTIVE']).optional(),
});

export type ServiceAccountCreateInput = z.infer<typeof serviceAccountCreateSchema>;
export type ServiceAccountUpdateInput = z.infer<typeof serviceAccountUpdateSchema>;
